import { useEffect, useRef, useState } from "react";
import { useT } from "../i18n";

/**
 * The feature clips are real recordings of the app on the S9+ with every tap
 * ringed in post (~/Documents/sageleaf-ads/features, README there), scaled to
 * 540 px wide and muted for the page. Each poster is the clip's last frame,
 * the result, so a visitor who never presses play still sees what it does.
 */
const CLIPS = "/landing/features";
// One per entry of t.landing.clips, in order.
const CLIP_NAMES = ["1-ask", "2-followup", "3-simpler", "4-meaning", "5-remember", "6-collections"];

const clipName = (i: number) => CLIP_NAMES[i] ?? "";

function reducedMotion() {
  return (
    typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

/**
 * Clip is one recording in a phone frame. Nothing is fetched until it comes
 * near the screen (preload="none" and no src before then); it plays while at
 * least half of it is in view and pauses when it leaves. With reduced motion
 * it never starts on its own: the poster stays, with a play button. It plays
 * once; onEnded hands over to the next feature.
 */
function Clip({
  name,
  alt,
  onEnded,
  className = "",
}: {
  name: string;
  alt: string;
  onEnded?: () => void;
  className?: string;
}) {
  const t = useT();
  const ref = useRef<HTMLVideoElement>(null);
  const [near, setNear] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [auto] = useState(() => !reducedMotion());
  const visible = useRef(false);

  useEffect(() => {
    const video = ref.current;
    if (!video) return;
    const nearObs = new IntersectionObserver(
      ([e]) => {
        if (e?.isIntersecting) {
          setNear(true);
          nearObs.disconnect();
        }
      },
      { rootMargin: "400px 0px" },
    );
    const viewObs = new IntersectionObserver(
      ([e]) => {
        visible.current = (e?.intersectionRatio ?? 0) >= 0.5;
        if (!visible.current) video.pause();
        else if (auto) video.play().catch(() => {});
      },
      { threshold: [0, 0.5] },
    );
    nearObs.observe(video);
    viewObs.observe(video);
    return () => {
      nearObs.disconnect();
      viewObs.disconnect();
    };
  }, [auto]);

  // A new clip in the same frame (another tab) starts from its beginning.
  useEffect(() => {
    const video = ref.current;
    if (!video || !near) return;
    video.load();
    if (auto && visible.current) video.play().catch(() => {});
  }, [name, near, auto]);

  const toggle = () => {
    const video = ref.current;
    if (!video) return;
    setNear(true);
    if (video.paused) video.play().catch(() => {});
    else video.pause();
  };

  return (
    <div className={`device ${className}`}>
      <div className="device-body">
        <span className="device-camera" aria-hidden="true" />
        <div className="device-screen device-screen--clip">
          <video
            ref={ref}
            src={near ? `${CLIPS}/${name}.mp4` : undefined}
            poster={`${CLIPS}/${name}.webp`}
            preload="none"
            muted
            playsInline
            aria-label={alt}
            data-testid="feature-clip"
            onPlay={() => setPlaying(true)}
            onPause={() => setPlaying(false)}
            onEnded={onEnded}
            onClick={toggle}
          />
          <button
            type="button"
            onClick={toggle}
            aria-label={playing ? t.landing.clipPause : t.landing.clipPlay}
            className={`clip-play ${playing ? "clip-play--playing" : ""}`}
          >
            <span aria-hidden="true">{playing ? "❚❚" : "▶"}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * FeatureClips is the landing page's "See it work": one phone, the features as
 * pills above it (a list beside it on a wide screen), the owner's pick of three
 * layouts (requirement 0144). Each clip plays once and hands over to the next,
 * so a visitor who only watches sees all six.
 */
export default function FeatureClips() {
  const t = useT();
  const [active, setActive] = useState(0);
  const clips = t.landing.clips;
  const next = () => setActive((a) => (a + 1) % clips.length);
  return (
    <section className="px-5 pt-14 pb-6" data-testid="landing-clips">
      <h2 className="vintage-serif text-center text-[1.8rem] font-bold sm:text-4xl">
        {t.landing.clipsTitle}
      </h2>
      <div className="mx-auto mt-6 grid max-w-4xl items-start gap-6 md:grid-cols-[1fr_auto] md:gap-14">
        <div>
          <ul className="flex flex-wrap justify-center gap-2 md:hidden" role="tablist">
            {clips.map((clip, i) => (
              <li key={clip.title}>
                <button
                  type="button"
                  role="tab"
                  aria-selected={i === active}
                  onClick={() => setActive(i)}
                  data-testid="clip-tab"
                  className={`clip-pill ${i === active ? "clip-pill--on" : ""}`}
                >
                  {clip.title}
                </button>
              </li>
            ))}
          </ul>
          <ol className="hidden md:block" role="tablist">
            {clips.map((clip, i) => (
              <li key={clip.title}>
                <button
                  type="button"
                  role="tab"
                  aria-selected={i === active}
                  onClick={() => setActive(i)}
                  className={`clip-row ${i === active ? "clip-row--on" : ""}`}
                >
                  <Ordinal i={i} />
                  <span className="vintage-serif block text-xl font-bold">{clip.title}</span>
                  {i === active && (
                    <span className="mt-1 block leading-7 text-[var(--v-soft)]">{clip.body}</span>
                  )}
                </button>
              </li>
            ))}
          </ol>
        </div>
        <div className="text-center">
          <Clip
            name={clipName(active)}
            alt={clips[active]?.title ?? ""}
            onEnded={next}
            className="mx-auto w-[15rem] sm:w-[16.5rem]"
          />
          <p className="mx-auto mt-4 max-w-xs text-[1.05rem] leading-7 text-[var(--v-soft)] md:hidden">
            {clips[active]?.body}
          </p>
        </div>
      </div>
    </section>
  );
}

function Ordinal({ i }: { i: number }) {
  return (
    <span className="vintage-serif text-sm tracking-[0.2em] text-[var(--v-brass)]">
      {String(i + 1).padStart(2, "0")}
    </span>
  );
}
